import React, { useState, useCallback, useEffect } from "react";
import Modal from "./Modal";
import styles from "./MoreFiltersModal.module.css";
import { useMatchBreakpoint } from "../../../hooks/useMatchBreakpoint";
import { luxuryBrands, premiumBrands, passionFilters } from "../constants";

const MoreFiltersModal = ({
  isOpen,
  onClose,
  onApplyFilters,
  initialFilters = {},
  fullWidth = false,
  bottomPosition = false,
  animate = true,
}) => {
  // Check if screen is under 700px
  const isSmallScreen = useMatchBreakpoint(700);

  // Force fullWidth and bottomPosition to true on small screens
  const shouldUseFullWidth = isSmallScreen || fullWidth;
  const shouldUseBottomPosition = isSmallScreen || bottomPosition;

  const [selectedPassions, setSelectedPassions] = useState(
    initialFilters.passions && initialFilters.passions.length > 0
      ? [initialFilters.passions[0]]
      : [],
  );
  const [selectedLuxuryBrands, setSelectedLuxuryBrands] = useState(
    initialFilters.luxuryBrands || [],
  );
  const [selectedPremiumBrands, setSelectedPremiumBrands] = useState(
    initialFilters.premiumBrands || [],
  );

  // Sync local state with initialFilters when it changes (e.g., when filters are removed via chips)
  useEffect(() => {
    setSelectedPassions(
      initialFilters.passions && initialFilters.passions.length > 0
        ? [initialFilters.passions[0]]
        : [],
    );
    setSelectedLuxuryBrands(initialFilters.luxuryBrands || []);
    setSelectedPremiumBrands(initialFilters.premiumBrands || []);
  }, [initialFilters]);

  const handlePassionToggle = useCallback((passionId) => {
    setSelectedPassions(
      (prev) =>
        prev.includes(passionId)
          ? [] // Deselect if already selected
          : [passionId], // Select only this one, replacing any previous selection
    );
  }, []);

  const handleLuxuryBrandToggle = useCallback((brandId) => {
    setSelectedLuxuryBrands((prev) =>
      prev.includes(brandId)
        ? prev.filter((id) => id !== brandId)
        : [...prev, brandId],
    );
  }, []);

  const handlePremiumBrandToggle = useCallback((brandId) => {
    setSelectedPremiumBrands((prev) =>
      prev.includes(brandId)
        ? prev.filter((id) => id !== brandId)
        : [...prev, brandId],
    );
  }, []);

  const handleSelectAllPassions = () => {
    // Since only one passion can be selected, this button will clear the selection
    setSelectedPassions([]);
  };

  const handleSelectAllLuxuryBrands = () => {
    console.log(luxuryBrands);
    if (selectedLuxuryBrands.length === luxuryBrands.length) {
      setSelectedLuxuryBrands([]);
    } else {
      setSelectedLuxuryBrands(luxuryBrands.map((b) => b.id));
    }
  };

  const handleSelectAllPremiumBrands = () => {
    if (selectedPremiumBrands.length === premiumBrands.length) {
      setSelectedPremiumBrands([]);
    } else {
      setSelectedPremiumBrands(premiumBrands.map((b) => b.id));
    }
  };

  const handleClose = () => {
    const filters = {
      passions: selectedPassions,
      luxuryBrands: selectedLuxuryBrands,
      premiumBrands: selectedPremiumBrands,
    };
    console.log(filters);
    if (onApplyFilters) {
      onApplyFilters(filters);
    }

    onClose();
  };

  const renderFilterGrid = (
    items,
    selectedItems,
    onToggle,
    type = "default",
  ) => (
    <div className={styles.filterGrid}>
      {items.map((item) => {
        if (type === "brand") {
          // For brands, just render the image with border selection
          return (
            <div
              key={item.id}
              className={styles.brandImageContainer}
              onClick={() => onToggle(item.id)}
            >
              <img
                src={item.icon}
                alt={item.name}
                className={`${styles.brandImage} ${selectedItems.includes(item.id) ? styles.selectedBrandImage : ""}`}
              />
            </div>
          );
        }

        // For passions, keep the existing button structure
        return (
          <button
            key={item.id}
            className={`${styles.filterItem} ${selectedItems.includes(item.id) ? styles.selected : ""}`}
            onClick={() => onToggle(item.id)}
            type="button"
          >
            {item.icon && (
              <img
                src={item.icon}
                alt={item.name}
                className={styles.passionIcon}
              />
            )}
            <span className={styles.filterText}>{item.name}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="More Filters"
      showCloseButton={false}
      fullWidth={shouldUseFullWidth}
      bottomPosition={shouldUseBottomPosition}
      animate={animate}
    >
      <div className={styles.filtersContainer}>
        {/* Passions Section */}
        <div className={styles.filterSection}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>FILTER BY PASSION</h3>
            <button
              className={styles.selectAllButton}
              onClick={handleSelectAllPassions}
              type="button"
              disabled={selectedPassions.length === 0}
            >
              {selectedPassions.length > 0 ? "Clear Selection" : "Select One"}
            </button>
          </div>
          {renderFilterGrid(
            passionFilters,
            selectedPassions,
            handlePassionToggle,
            "passion",
          )}
        </div>

        {/* Brand Section */}
        <div className={styles.filterSection}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>FILTER BY BRAND</h3>
          </div>

          {/* Luxury Brands */}
          <div className={styles.brandSubsection}>
            <div className={styles.brandHeader}>
              <h4 className={styles.brandTitle}>LUXURY BRANDS</h4>
              <button
                className={styles.selectAllButton}
                onClick={handleSelectAllLuxuryBrands}
                type="button"
              >
                {selectedLuxuryBrands.length === luxuryBrands.length
                  ? "Deselect All"
                  : "Select All"}
              </button>
            </div>
            {renderFilterGrid(
              luxuryBrands,
              selectedLuxuryBrands,
              handleLuxuryBrandToggle,
              "brand",
            )}
          </div>

          {/* Premium Brands */}
          <div className={styles.brandSubsection}>
            <div className={styles.brandHeader}>
              <h4 className={styles.brandTitle}>PREMIUM BRANDS</h4>
              <button
                className={styles.selectAllButton}
                onClick={handleSelectAllPremiumBrands}
                type="button"
              >
                {selectedPremiumBrands.length === premiumBrands.length
                  ? "Deselect All"
                  : "Select All"}
              </button>
            </div>
            {renderFilterGrid(
              premiumBrands,
              selectedPremiumBrands,
              handlePremiumBrandToggle,
              "brand",
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className={styles.modalActions}>
          <button
            className={styles.resetButton}
            onClick={handleClose}
            type="button"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default MoreFiltersModal;
